import { useState } from "react";
import { Link } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowRight, CalendarDays, ClipboardCheck } from "lucide-react";
import { Card, Skeleton } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { useIsMobile } from "@/lib/use-media-query";
import { agendaViewDays, agendaViewRange, type AgendaViewMode, type WeekAppointment } from "@/lib/dashboard-week";
import type { ClinicOutgoingOrder } from "@/lib/dashboard-lab";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";
import { MonthCalendarGrid } from "./MonthCalendarGrid";
import { SemanaComDiaSelecionado } from "./SemanaComDiaSelecionado";
import { AgendaList } from "./WeekAgenda";
import { AppointmentDetailDialog } from "./AppointmentDetailDialog";
import { ResumoDePedidosDaClinica } from "./ResumoDePedidosDaClinica";

type AgendaKind = "consultas" | "pedidos";

const KIND_OPTIONS: Array<{ key: AgendaKind; label: string; icon: typeof CalendarDays }> = [
  { key: "consultas", label: "Consultas", icon: CalendarDays },
  { key: "pedidos", label: "Pedidos", icon: ClipboardCheck },
];

const VIEW_OPTIONS: Array<{ key: AgendaViewMode; label: string }> = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mês" },
];

const VIEW_TITLE: Record<AgendaViewMode, string> = {
  day: "Agenda do dia",
  week: "Agenda da semana",
  month: "Agenda do mês",
};

function rangeLabel(mode: AgendaViewMode): string {
  const { start, end } = agendaViewRange(mode);
  if (mode === "day") {
    const label = format(start, "EEEE, d 'de' MMMM", { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  if (mode === "month") {
    const label = format(start, "MMMM 'de' yyyy", { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  return `${format(start, "d", { locale: ptBR })}–${format(end, "d 'de' MMM", { locale: ptBR })}`;
}

type Props = {
  appts: WeekAppointment[];
  loading: boolean;
  viewMode: AgendaViewMode;
  onViewModeChange: (mode: AgendaViewMode) => void;
  onSelectForeign?: (appt: WeekAppointment) => void;
};

/** Card único de agenda do Início — toggle Consultas/Pedidos entre o título e o Dia/Semana/Mês.
 *  Reaproveita os mesmos sub-componentes de view que WeekAgenda/OrdersAgenda já usavam
 *  separadamente; view mode e "Mostrar tudo" são estado compartilhado entre as duas abas.
 *  Mês é sempre densidade (quantas por dia); o detalhe dia a dia é da Semana. */
export function InicioAgendaCard({ appts, loading, viewMode, onViewModeChange, onSelectForeign }: Props) {
  const ehCelular = useIsMobile();
  const [kind, setKind] = useState<AgendaKind>("consultas");
  // Ligado por padrão (decisão de produto, 2026-08): quem abre o Início quer ver AS consultas,
  // não só quantas são — desligar vira o opt-in da leitura de densidade.
  const [mostrarTudo, setMostrarTudo] = useState(true);
  const [selectedAppt, setSelectedAppt] = useState<WeekAppointment | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const days = agendaViewDays(viewMode);

  const ordersQuery = trpc.prosthesis.listMineOutgoingDeadlineSummary.useQuery();
  const orders = (ordersQuery.data ?? []) as ClinicOutgoingOrder[];

  const handleSelectAppt = (appt: WeekAppointment) => {
    if (appt.ehDeOutraOrganizacao && onSelectForeign) {
      onSelectForeign(appt);
      return;
    }
    setSelectedAppt(appt);
  };
  const handleSelectOrder = (order: ClinicOutgoingOrder) => setSelectedOrderId(order.id);

  const isLoading = kind === "consultas" ? loading : ordersQuery.isLoading;

  // No celular o link fecha o card, depois do conteúdo — no topo ele ficava solto entre as
  // segmentações e a lista, quebrando a leitura. No desktop segue à direita do cabeçalho.
  const linkVerTudo = (
    <Link
      to={kind === "consultas" ? "/agenda" : "/protese"}
      className="-mx-2 inline-flex h-11 items-center gap-1 rounded-md px-2 text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 sm:mx-0 sm:h-auto sm:px-0 dark:text-primary"
    >
      {kind === "consultas" ? "Ver agenda completa" : "Ver todos os pedidos"}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );

  return (
    <Card className="p-5 sm:p-6">
      <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[350px_1fr_auto_1fr]">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary">
            {kind === "consultas" ? <CalendarDays className="h-5 w-5" /> : <ClipboardCheck className="h-5 w-5" />}
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">
              {kind === "consultas" ? VIEW_TITLE[viewMode] : "Resumo dos pedidos"}
            </h2>
            {/* Sem `capitalize`: a classe maiusculiza TODA palavra e produzia "16–22 De Ago" e
                "Quinta-Feira, 20 De Agosto". Quem já põe maiúscula só na primeira letra é o
                `rangeLabel`, do jeito que o português pede. */}
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">
              {kind === "consultas" ? rangeLabel(viewMode) : "Pelo dia de entrega pedido"}
            </p>
          </div>
        </div>

        <div className="flex w-full justify-start sm:justify-center">
          <div className="flex h-12 w-full items-center rounded-md border border-zinc-200 bg-white p-0.5 sm:inline-flex sm:h-9 sm:w-auto dark:border-border dark:bg-card">
            {KIND_OPTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setKind(key)}
                className={cn(
                  "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded px-2 text-sm font-medium transition-colors sm:h-8 sm:flex-none sm:px-3",
                  kind === key
                    ? "bg-brand-600 font-semibold text-white"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Dia/Semana/Mês é só da agenda de consultas; nos pedidos o espaço fica reservado pra o
            cabeçalho não pular de lugar ao trocar de aba. */}
        <div
          className={cn(
            "w-full justify-start sm:justify-center",
            kind === "consultas" ? "flex" : "hidden sm:invisible sm:flex",
          )}
        >
          <div className="flex h-12 w-full items-center rounded-md border border-zinc-200 bg-white p-0.5 sm:inline-flex sm:h-9 sm:w-auto dark:border-border dark:bg-card">
            {VIEW_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => onViewModeChange(key)}
                className={cn(
                  "inline-flex h-11 flex-1 items-center justify-center rounded text-sm font-medium transition-colors sm:h-8 sm:w-20 sm:flex-none",
                  viewMode === key
                    ? "bg-brand-600 font-semibold text-white"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="hidden items-center justify-end sm:flex">{linkVerTudo}</div>
      </div>

      {/* "Mostrar tudo" é só do Mês no desktop: a Semana virou seletor de dia + cartões (sempre
          detalhados, o toggle não teria o que alternar), e no celular a célula do mês tem ~45px,
          onde pílula por dia não cabe. */}
      {!isLoading &&
        kind === "consultas" &&
        viewMode === "month" &&
        !ehCelular && (
        <label className="-mx-2 mt-4 flex h-11 w-fit items-center gap-2 rounded-md px-2 text-xs font-medium text-zinc-600 sm:mx-0 sm:h-auto sm:px-0 sm:py-1 dark:text-muted-foreground">
          <input
            type="checkbox"
            checked={mostrarTudo}
            onChange={(e) => setMostrarTudo(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
          />
          Mostrar tudo
        </label>
      )}

      <div className="mt-3 space-y-3 sm:mt-5 sm:space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : kind === "consultas" ? (
          viewMode === "week" ? (
            <SemanaComDiaSelecionado days={days} appts={appts} onSelectAppt={handleSelectAppt} />
          ) : viewMode === "month" ? (
            <MonthCalendarGrid appts={appts} showAll={!ehCelular && mostrarTudo} onSelectAppt={handleSelectAppt} />
          ) : (
            <AgendaList days={days} appts={appts} viewMode={viewMode} onSelectAppt={handleSelectAppt} />
          )
        ) : (
          <ResumoDePedidosDaClinica orders={orders} onSelectOrder={handleSelectOrder} />
        )}
      </div>

      <div className="mt-2 flex justify-start sm:hidden">{linkVerTudo}</div>

      <AppointmentDetailDialog appointment={selectedAppt} onClose={() => setSelectedAppt(null)} />
      <ProsthesisOrderDetailDialog orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} readOnly />
    </Card>
  );
}
